
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { 
  BookOpen, 
  ExternalLink, 
  Tag, 
  Calendar,
  FileText,
  AlertTriangle,
  Database,
  Users,
  TrendingUp
} from "lucide-react";
import { MedicalKnowledgeEntry } from "@/services/medicalKnowledge";
import EvidenceQualityIndicator from "./EvidenceQualityIndicator";

interface MedicalKnowledgeDisplayProps {
  title: string;
  entries: MedicalKnowledgeEntry[];
  icon: React.ComponentType<{ className?: string }>;
  color: string;
  maxDisplay?: number;
  showFullDetails?: boolean;
}

const MedicalKnowledgeDisplay = ({ 
  title, 
  entries, 
  icon: IconComponent, 
  color,
  maxDisplay = 5,
  showFullDetails = false
}: MedicalKnowledgeDisplayProps) => {
  if (entries.length === 0) return null;

  const displayEntries = entries.slice(0, maxDisplay);
  
  const getImpactScore = (entry: MedicalKnowledgeEntry): number => {
    // Calculate impact based on evidence level, recency, and category
    let score = 0;
    
    if (entry.evidence_level === 'high') score += 40;
    else if (entry.evidence_level === 'medium') score += 25;
    else score += 10;
    
    // Recency bonus (within last 5 years)
    if (entry.publication_date) {
      const publicationYear = new Date(entry.publication_date).getFullYear();
      const currentYear = new Date().getFullYear();
      const yearsDiff = currentYear - publicationYear;
      if (yearsDiff <= 5) score += 30;
      else if (yearsDiff <= 10) score += 15;
    }
    
    // Category importance
    if (entry.medical_category === 'clinical_guidelines') score += 20;
    else if (entry.medical_category === 'diagnosis') score += 15;
    
    return Math.min(score, 100);
  };

  const getCategoryBadgeColor = (category?: string) => {
    if (!category) return 'bg-gray-100 text-gray-800';
    
    const categoryColors: Record<string, string> = {
      'diagnosis': 'bg-blue-100 text-blue-800',
      'treatment': 'bg-green-100 text-green-800',
      'drug_safety': 'bg-red-100 text-red-800',
      'clinical_guidelines': 'bg-purple-100 text-purple-800',
      'research': 'bg-indigo-100 text-indigo-800',
      'emergency_medicine': 'bg-orange-100 text-orange-800'
    };
    
    return categoryColors[category.toLowerCase()] || 'bg-gray-100 text-gray-800';
  };

  const truncateText = (text: string, maxLength: number = 250) => {
    if (text.length <= maxLength) return text;
    return text.slice(0, maxLength) + '...';
  };

  // Sort entries by impact score for better presentation
  const sortedEntries = [...displayEntries].sort((a, b) => 
    getImpactScore(b) - getImpactScore(a)
  );

  return (
    <Card className={`p-6 bg-white/95 backdrop-blur-sm border-${color}-200 shadow-lg`}>
      <div className="flex items-center justify-between mb-6">
        <h3 className="text-xl font-semibold text-gray-900 flex items-center">
          <IconComponent className={`mr-3 h-6 w-6 text-${color}-600`} />
          {title}
        </h3>
        <div className="flex items-center space-x-2">
          <Badge className={`bg-${color}-100 text-${color}-800 font-medium`}>
            {entries.length} {entries.length === 1 ? 'Entry' : 'Entries'}
          </Badge>
          {entries.length > 0 && (
            <Badge variant="outline" className="text-xs">
              <TrendingUp className="w-3 h-3 mr-1" />
              Evidence Score: {Math.round(sortedEntries.reduce((acc, entry) => acc + getImpactScore(entry), 0) / sortedEntries.length)}
            </Badge>
          )}
        </div>
      </div>
      
      <div className="space-y-4">
        {sortedEntries.map((entry, index) => {
          const impactScore = getImpactScore(entry);
          
          return (
            <div key={entry.id} className={`border-l-4 border-${color}-300 rounded-lg p-5 bg-gradient-to-r from-${color}-50/30 to-white hover:shadow-md transition-all duration-200`}>
              <div className="space-y-4">
                {/* Header with title, impact score and evidence quality */}
                <div className="flex items-start justify-between">
                  <div className="flex-1 pr-4">
                    <div className="flex items-center space-x-2 mb-2">
                      <h4 className="font-semibold text-gray-900 leading-tight">
                        {entry.title}
                      </h4>
                      <Badge className={`text-xs ${impactScore >= 70 ? 'bg-green-100 text-green-800' : 
                                      impactScore >= 50 ? 'bg-yellow-100 text-yellow-800' : 
                                      'bg-gray-100 text-gray-800'}`}>
                        Impact: {impactScore}
                      </Badge>
                    </div>
                    {showFullDetails && entry.summary && (
                      <p className="text-sm text-gray-700 leading-relaxed mb-3">
                        {truncateText(entry.summary)}
                      </p>
                    )}
                  </div>
                  <div className="flex-shrink-0">
                    <EvidenceQualityIndicator
                      evidenceLevel={entry.evidence_level as 'high' | 'medium' | 'low'}
                      publicationDate={entry.publication_date || undefined}
                      source={entry.source}
                      studyType={entry.medical_category}
                    />
                  </div>
                </div>

                {/* Summary for non-detailed view */}
                {!showFullDetails && entry.summary && (
                  <p className="text-sm text-gray-700 leading-relaxed">
                    {truncateText(entry.summary, 150)}
                  </p>
                )}

                {/* Categories and Professional Tags */}
                <div className="space-y-3">
                  {entry.medical_category && (
                    <div className="flex items-center space-x-2">
                      <Tag className="w-4 h-4 text-gray-500" />
                      <Badge className={getCategoryBadgeColor(entry.medical_category)}>
                        {entry.medical_category.replace('_', ' ').toUpperCase()}
                      </Badge>
                      {entry.medical_category === 'clinical_guidelines' && (
                        <Badge className="bg-indigo-100 text-indigo-800 text-xs">
                          <Users className="w-3 h-3 mr-1" />
                          Professional Standard
                        </Badge>
                      )}
                    </div>
                  )}

                  {/* Clinical Context Tags */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    {/* Condition Tags */}
                    {entry.condition_tags && entry.condition_tags.length > 0 && (
                      <div className="flex flex-wrap gap-1">
                        <span className="text-xs text-gray-600 mr-2 flex items-center font-medium">
                          <FileText className="w-3 h-3 mr-1" />
                          Conditions:
                        </span>
                        {entry.condition_tags.slice(0, 3).map((condition, idx) => (
                          <Badge key={idx} variant="outline" className="text-xs bg-blue-50 border-blue-200">
                            {condition}
                          </Badge>
                        ))}
                        {entry.condition_tags.length > 3 && (
                          <Badge variant="outline" className="text-xs">
                            +{entry.condition_tags.length - 3} more
                          </Badge>
                        )}
                      </div>
                    )}

                    {/* Symptom Tags */}
                    {entry.symptom_tags && entry.symptom_tags.length > 0 && (
                      <div className="flex flex-wrap gap-1">
                        <span className="text-xs text-gray-600 mr-2 flex items-center font-medium">
                          <AlertTriangle className="w-3 h-3 mr-1" />
                          Symptoms:
                        </span>
                        {entry.symptom_tags.slice(0, 3).map((symptom, idx) => (
                          <Badge key={idx} variant="secondary" className="text-xs bg-orange-50 border-orange-200">
                            {symptom}
                          </Badge>
                        ))}
                        {entry.symptom_tags.length > 3 && (
                          <Badge variant="secondary" className="text-xs">
                            +{entry.symptom_tags.length - 3} more
                          </Badge>
                        )}
                      </div>
                    )}
                  </div>

                  {/* Keywords - Professional Context */}
                  {entry.keywords && entry.keywords.length > 0 && (
                    <div className="flex flex-wrap gap-1">
                      <span className="text-xs text-gray-500 mr-2 font-medium">Medical Keywords:</span>
                      {entry.keywords.slice(0, 6).map((keyword, idx) => (
                        <Badge key={idx} variant="outline" className="text-xs bg-gray-50 hover:bg-gray-100 transition-colors">
                          {keyword}
                        </Badge>
                      ))}
                      {entry.keywords.length > 6 && (
                        <Badge variant="outline" className="text-xs">
                          +{entry.keywords.length - 6} more
                        </Badge>
                      )}
                    </div>
                  )}
                </div>

                {/* Professional Reference and External Links */}
                {entry.external_id && (
                  <div className="pt-3 border-t border-gray-100">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center text-xs text-gray-500">
                        <Database className="w-3 h-3 mr-1" />
                        <span className="font-mono">ID: {entry.external_id}</span>
                      </div>
                      {entry.source === 'pubmed' && (
                        <a 
                          href={`https://pubmed.ncbi.nlm.nih.gov/${entry.external_id}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center text-blue-600 hover:text-blue-800 text-xs font-medium transition-colors"
                        >
                          <ExternalLink className="w-3 h-3 mr-1" />
                          View on PubMed
                        </a>
                      )}
                      {entry.source === 'trip' && (
                        <a 
                          href={`https://www.tripdatabase.com/doc/${entry.external_id}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center text-purple-600 hover:text-purple-800 text-xs font-medium transition-colors"
                        >
                          <ExternalLink className="w-3 h-3 mr-1" />
                          View on TRIP Database
                        </a>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>
          );
        })}
        
        {entries.length > maxDisplay && (
          <div className="text-center pt-6 border-t border-gray-200">
            <div className="bg-gradient-to-r from-blue-50 to-indigo-50 p-4 rounded-lg">
              <p className="text-sm text-gray-700 mb-2">
                <strong>Additional Evidence Available:</strong> Showing {maxDisplay} of {entries.length} entries
              </p>
              <p className="text-xs text-gray-600">
                Complete literature review includes {entries.length - maxDisplay} additional peer-reviewed sources
              </p>
            </div>
          </div>
        )}
      </div>
    </Card>
  );
};

export default MedicalKnowledgeDisplay;
